import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from '@mui/material';
import Button from '../Button/Button';
import { useWebSocket } from '../../context/WebSocket/WebSocketContext';
import { useBulkCreateTakedownsMutation } from '../../queries/takedowns';
import { showToast } from '../../store/toaster/action';
import {
  addPendingBulkTakedowns,
  removePendingBulkTakedowns,
} from '../../store/pendingBulkTakedowns/slice';
import { WEB_SOCKET_CHANNELS } from '../../utils';
import {
  BulkTakedownUpdatePayload,
  CreateTakedownType,
  WebSocketMessage,
} from '../../helpers/types';

export interface BulkTakedownItem {
  threatId: number;
  protectedAssetId: number;
  threatName: string;
}

interface BulkTakedownModalProps {
  /** Eligible (confirmed, deduped) threats to take down. The caller must
   * keep this referentially stable while the modal is mounted. */
  items: BulkTakedownItem[];
  /** Selected threats excluded because they are not confirmed. */
  skippedCount?: number;
  onClose: () => void;
  /** Called once when every item has settled (created or failed). */
  onComplete?: () => void;
}

/** How long we wait for all per-item WS results before telling the user to
 * check the Takedowns page later. */
const PROCESSING_TIMEOUT_MS = 2 * 60 * 1000;

const itemKey = (attackId: number, brandId: number) => `${attackId}_${brandId}`;

/** Confirm-and-progress modal for bulk takedown initiation (WHEEL-195).
 * POST v2/takedown/accept/bulk-job returns immediately; one result per item
 * then arrives on the `bulk_takedown` WS channel. The socket carries results
 * for ALL the user's in-flight bulk jobs, so messages are matched against
 * this modal's own request set before counting. */
const BulkTakedownModal = ({
  items,
  skippedCount = 0,
  onClose,
  onComplete,
}: BulkTakedownModalProps) => {
  const dispatch = useDispatch();
  const { subscribe, status: socketStatus } = useWebSocket();

  const [isRequested, setIsRequested] = useState(false);
  const [isTimedOut, setIsTimedOut] = useState(false);
  const [bulkProgress, setBulkProgress] = useState({ created: 0, failed: 0 });

  const settledKeysRef = useRef<Set<string>>(new Set());
  const isCompletedRef = useRef(false);
  const processingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  const bulkCreateTakedowns = useBulkCreateTakedownsMutation();

  const total = items.length;
  const completedRequests = bulkProgress.created + bulkProgress.failed;
  const progress =
    total > 0 ? Math.round((completedRequests / total) * 100) : 0;
  const isProcessingComplete = isRequested && completedRequests === total;
  const isSocketConnected = socketStatus === 'connected';

  const handleConfirm = () => {
    const payload: CreateTakedownType[] = items.map(item => ({
      attack_id: item.threatId,
      brand_id: item.protectedAssetId,
    }));
    // Queue the request set so BulkTakedownNotifier can turn failures into
    // notification-drawer entries even after this modal unmounts.
    const pendingItems = items.map(item => ({
      attackId: item.threatId,
      brandId: item.protectedAssetId,
      threatName: item.threatName,
      startedAt: new Date().toISOString(),
    }));
    dispatch(addPendingBulkTakedowns(pendingItems));
    setIsRequested(true);
    bulkCreateTakedowns.mutate(payload, {
      onSuccess: () => {
        processingTimeoutRef.current = setTimeout(() => {
          setIsTimedOut(true);
        }, PROCESSING_TIMEOUT_MS);
      },
      onError: () => {
        // Request-level failure (toast comes from the mutation hook): no
        // per-item results will arrive, so roll the queue back and return
        // to the confirm phase so the user can retry.
        dispatch(
          removePendingBulkTakedowns(
            pendingItems.map(({ attackId, brandId }) => ({
              attackId,
              brandId,
            })),
          ),
        );
        setIsRequested(false);
      },
    });
  };

  const handleTakedownUpdate = useCallback(
    (message: WebSocketMessage<BulkTakedownUpdatePayload>) => {
      const data = message?.data;
      if (!data) return;
      const belongsToThisRequest = items.some(
        item =>
          item.threatId === data.attack_id &&
          item.protectedAssetId === data.brand_id,
      );
      if (!belongsToThisRequest) return;
      const key = itemKey(data.attack_id, data.brand_id);
      if (settledKeysRef.current.has(key)) return;
      settledKeysRef.current.add(key);
      setBulkProgress(prev =>
        data.error
          ? { ...prev, failed: prev.failed + 1 }
          : { ...prev, created: prev.created + 1 },
      );
    },
    [items],
  );

  useEffect(
    () => subscribe(WEB_SOCKET_CHANNELS.BULK_TAKEDOWN, handleTakedownUpdate),
    [subscribe, handleTakedownUpdate],
  );

  // Completion: fire the summary toast and notify the caller exactly once.
  useEffect(() => {
    if (!isProcessingComplete || isCompletedRef.current) return;
    isCompletedRef.current = true;
    if (processingTimeoutRef.current) {
      clearTimeout(processingTimeoutRef.current);
      processingTimeoutRef.current = null;
    }
    const { created, failed } = bulkProgress;
    if (failed > 0) {
      dispatch(
        showToast(
          `${created} takedown(s) started successfully. ${failed} failed.`,
          'warning',
        ),
      );
    } else {
      dispatch(
        showToast(`${created} takedown(s) started successfully.`, 'success'),
      );
    }
    onComplete?.();
  }, [isProcessingComplete, bulkProgress, dispatch, onComplete]);

  useEffect(
    () => () => {
      if (processingTimeoutRef.current) {
        clearTimeout(processingTimeoutRef.current);
      }
    },
    [],
  );

  return (
    <Dialog
      open
      onClose={isRequested ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      data-testid="bulk-takedown-modal"
    >
      <DialogTitle>
        {total > 1 ? 'Confirm takedowns' : 'Confirm takedown'}
      </DialogTitle>
      <DialogContent>
        {!isRequested ? (
          <>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {total} takedown{total > 1 ? 's' : ''} will be initiated
              immediately upon confirmation.
            </Typography>
            {skippedCount > 0 && (
              <Typography
                variant="body2"
                sx={{ color: 'text.secondary', mt: 1 }}
                data-testid="bulk-takedown-skipped-note"
              >
                {skippedCount} selected threat{skippedCount > 1 ? 's' : ''}{' '}
                without confirmed status will be skipped.
              </Typography>
            )}
          </>
        ) : (
          <>
            <Typography variant="body2" sx={{ color: 'text.primary' }}>
              {isProcessingComplete
                ? 'Done.'
                : `Creating takedown${
                    total > 1 ? 's' : ''
                  }. You can safely navigate away.`}
            </Typography>
            <Box
              sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 2 }}
              data-testid="bulk-takedown-progress"
            >
              <Box sx={{ flexGrow: 1 }}>
                <LinearProgress
                  variant={isSocketConnected ? 'determinate' : 'indeterminate'}
                  value={progress}
                />
              </Box>
              {isSocketConnected && (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {progress}%
                </Typography>
              )}
            </Box>
            {isTimedOut && !isProcessingComplete && (
              <Typography
                variant="body2"
                sx={{ color: 'error.main', mt: 2 }}
                data-testid="bulk-takedown-timeout-note"
              >
                Processing is taking longer than expected. Check the Takedowns
                page later for results.
              </Typography>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ padding: '0 24px 20px' }}>
        {!isRequested ? (
          <>
            <Button
              data-testid="bulk-takedown-cancel"
              variant="outlined"
              label="Cancel"
              onClick={onClose}
            />
            <Button
              data-testid="bulk-takedown-confirm"
              variant="contained"
              label={total > 1 ? 'Start takedowns' : 'Start takedown'}
              disabled={bulkCreateTakedowns.isPending || total === 0}
              onClick={handleConfirm}
            />
          </>
        ) : (
          <Button
            data-testid="bulk-takedown-close"
            variant="contained"
            label="Close"
            onClick={onClose}
          />
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkTakedownModal;
